import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';

import * as Api from '@vi-iot/client';

import { XMLHelperService } from '@services';
import { DevicesStore, RemoteDiagnosticsStore } from '@store';

import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_HYDRAULIC_SCHEME } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { VddDetailsListStore } from '../vdd-details-list';

import { HydraulicSchematicActions } from './hydraulic-schematic.actions';
import { HydraulicSchematicStore } from './hydraulic-schematic.store';

@Injectable()
export class HydraulicSchematicEffects {
  constructor(
    private actions$: Actions,
    private hydraulicSchematicApi: Api.HydraulicSchematicApi,
    private devicesStore: DevicesStore,
    private hydraulicSchematicStore: HydraulicSchematicStore,
    private vddDetailsListStore: VddDetailsListStore,
    private svgHelperService: XMLHelperService,
    private remoteDiagnosticsHelpersService: Api.RemoteDiagnosticsHelpersService,
    private appInsightsMonitoringService: AppInsightsMonitoringService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore
  ) {}

  getHydraulicSchematic$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(HydraulicSchematicActions.getHydraulicSchematic.attempt),
      concatLatestFrom(() => this.devicesStore.selectedDevice$),
      switchMap(([action, deviceDetails]) => {
        const schemeRequest = action.isInternalScheme
          ? this.hydraulicSchematicApi.getInternalSchematic(
              deviceDetails.boilerSerial,
              action.hydraMatrixCode,
              action.bundleId
            )
          : this.hydraulicSchematicApi.getSchematic(
              deviceDetails.boilerSerial,
              action.hydraMatrixCode,
              action.bundleId
            );

        return schemeRequest.pipe(
          map((hydraulicSchematicSvg) =>
            this.svgHelperService.getJsonFromXMLString(hydraulicSchematicSvg)
          ),
          map((hydraulicSchematicJson) => {
            const vievBoxValue = this.getViewBoxValueFromJson(
              hydraulicSchematicJson,
              action.isInternalScheme
            );

            return this.svgHelperService.addOrUpdateSvgAttributes(hydraulicSchematicJson, [
              { name: 'id', value: 'svgElement' },
              { name: 'viewBox', value: vievBoxValue },
              { name: 'height', value: '100%' },
              { name: 'width', value: '100%' },
              { name: 'preserveAspectRatio', value: 'xMidYMid meet' },
            ]);
          }),
          map((hydraulicSchematicJson) =>
            HydraulicSchematicActions.getHydraulicSchematic.success({
              hydraulicSchematicSvgModel: hydraulicSchematicJson,
            })
          ),
          catchError((error) => {
            this.appInsightsMonitoringService.logEvent(
              APP_INSIGHTS_HYDRAULIC_SCHEME.GET_HYDRAULIC_SCHEME_FAILED,
              LogLevelEnum.ERROR,
              { error }
            );

            return of(HydraulicSchematicActions.getHydraulicSchematic.error({ error }));
          })
        );
      })
    );
  });

  getHydraulicSchematicMapping$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(HydraulicSchematicActions.getHydraulicSchematicMapping.attempt),
      concatLatestFrom(() => this.devicesStore.selectedDevice$),
      switchMap(([_action, deviceDetails]) =>
        this.hydraulicSchematicApi.getSchematicMapping(deviceDetails.boilerSerial).pipe(
          map((hydraulicSchematicMapping) =>
            HydraulicSchematicActions.getHydraulicSchematicMapping.success({
              hydraulicSchematicMapping: hydraulicSchematicMapping?.mapping?.code,
            })
          ),
          catchError((error) =>
            of(HydraulicSchematicActions.getHydraulicSchematicMapping.error({ error }))
          ),
          takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
        )
      )
    );
  });

  readVddValuesForSvg$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(HydraulicSchematicActions.triggerReadVddValuesForSvg),
        concatLatestFrom(() => this.hydraulicSchematicStore.getHydraulicSchematicVdds$),
        filter(([_action, vddMappings]) => !!vddMappings),
        mergeMap(([action, vddMappings]) =>
          combineLatest([
            of(action),
            of(vddMappings),
            this.remoteDiagnosticsStore
              .getMainLogicalLinkFromTopology(action.gatewaySerial)
              .pipe(take(1)),
          ])
        ),
        map(([action, vddMappings, logicalLink]) =>
          this.vddDetailsListStore.getStructureAndValueForVddListAttempt(
            vddMappings.map((vddMapping) => vddMapping.feature),
            action.gatewaySerial,
            logicalLink
          )
        )
      );
    },
    { dispatch: false }
  );

  private getViewBoxValueFromJson(hydraulicJson: any, isInternalScheme: boolean): string {
    const margin = 5;
    let maximumY = 0;

    hydraulicJson.svg.g.forEach((entry) => {
      if (entry?.polyline?.length) {
        const polylines = entry.polyline;
        polylines.forEach((polyline) => {
          const drawFromTo = polyline['@_points'];
          const drawSegments = drawFromTo?.split(' ');

          if (drawSegments?.length) {
            drawSegments.forEach((segment) => {
              const y = +segment.split(',')[1];

              if (maximumY < y) {
                maximumY = y;
              }
            });
          }
        });
      }
    });

    let height = maximumY + margin;

    if (isInternalScheme) {
      height = height + 5;
    }

    return `0 0 250 ${height}`;
  }
}
